import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Check, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Unsubscribe — Bracket Goblin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type State =
  | { kind: "loading" }
  | { kind: "valid" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "submitting" }
  | { kind: "done" }
  | { kind: "error"; message: string };

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/email/unsubscribe?token=${encodeURIComponent(token)}`,
        );
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || json.error) {
          setState({ kind: "invalid" });
          return;
        }
        if (json.valid === false && json.reason === "already_unsubscribed") {
          setState({ kind: "already" });
          return;
        }
        if (json.valid === true) {
          setState({ kind: "valid" });
          return;
        }
        setState({ kind: "invalid" });
      } catch {
        if (!cancelled) setState({ kind: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    setState({ kind: "submitting" });
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const json = await res.json();
      if (!res.ok) {
        setState({ kind: "error", message: json.error ?? "Something went wrong" });
        return;
      }
      if (json.success === false && json.reason === "already_unsubscribed") {
        setState({ kind: "already" });
        return;
      }
      setState({ kind: "done" });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : "Network error",
      });
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-bold">
            🧌 Bracket Goblin
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          {state.kind === "loading" && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              <p className="text-sm text-muted-foreground">Checking your link…</p>
            </div>
          )}

          {state.kind === "valid" && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2.5 shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-bold text-foreground">
                    Unsubscribe from Bracket Goblin?
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                    You'll stop getting bracket reminders and any other emails
                    from us. You can still use your account normally — but the
                    Goblin won't poke you anymore. (Probably.)
                  </p>
                </div>
              </div>
              <Button onClick={confirm} className="w-full" size="lg">
                Yes, unsubscribe me
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Changed your mind? Just close this tab.
              </p>
            </div>
          )}

          {state.kind === "submitting" && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Unsubscribing…</p>
            </div>
          )}

          {state.kind === "done" && (
            <div className="text-center space-y-3 py-2">
              <div className="inline-flex rounded-full bg-success/10 p-3">
                <Check className="w-6 h-6 text-success" strokeWidth={3} />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground">
                You're unsubscribed
              </h1>
              <p className="text-sm text-muted-foreground">
                We won't send you any more emails. The Goblin sulks in the
                corner. If this was a mistake, ask your league admin to add
                you back.
              </p>
            </div>
          )}

          {state.kind === "already" && (
            <div className="text-center space-y-3 py-2">
              <div className="inline-flex rounded-full bg-muted p-3">
                <Check className="w-6 h-6 text-muted-foreground" strokeWidth={3} />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground">
                Already unsubscribed
              </h1>
              <p className="text-sm text-muted-foreground">
                This email is already on our suppression list. No further action needed.
              </p>
            </div>
          )}

          {state.kind === "invalid" && (
            <div className="text-center space-y-3 py-2">
              <div className="inline-flex rounded-full bg-destructive/10 p-3">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground">
                Link not valid
              </h1>
              <p className="text-sm text-muted-foreground">
                This unsubscribe link is missing or invalid. Open the most
                recent Bracket Goblin email and use the link at the bottom.
              </p>
            </div>
          )}

          {state.kind === "error" && (
            <div className="text-center space-y-3 py-2">
              <div className="inline-flex rounded-full bg-destructive/10 p-3">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-sm text-muted-foreground">{state.message}</p>
              <Button variant="outline" onClick={confirm}>
                Try again
              </Button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
